import React from 'react';
import PropTypes from 'prop-types';
import { InputBase } from '@material-ui/core';

// Icons
import SearchIcon from '@material-ui/icons/Search';

// Constants
import { T, FONT_MONO } from '../constants/tokens';

/**
 * The pieces every card list shares: the search field, the empty or loading
 * note, and the pager. Used by the Devices and Staff lists and by the room
 * list under the live map, so they look and behave the same.
 */

/** Cards per page, as the design pages its lists. */
export const PER_PAGE = 10;

/** The page of `rows` to show, with the page clamped into range. */
export function paginate(rows, page) {
  const pageCount = Math.max(1, Math.ceil(rows.length / PER_PAGE));
  const current = Math.min(Math.max(1, page), pageCount);
  return {
    page: current,
    pageCount,
    pageRows: rows.slice((current - 1) * PER_PAGE, current * PER_PAGE)
  };
}

/**
 * Whether two row lists hold the same records, by `idOf`. FilterWidget re-runs
 * on every data refresh, so "the filter result changed" has to be told apart
 * from "the same rows were fetched again" before resetting the page.
 */
export function sameRows(a, b, idOf) {
  if (a == null || b == null) return a === b;
  if (a.length !== b.length) return false;
  return a.every((row, i) => idOf(row) === idOf(b[i]));
}

export function SearchField({ value, onChange, placeholder = 'Search', label = 'search list', style = {} }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        height: 40,
        padding: '0 12px',
        background: T.elevated,
        border: '1px solid ' + T.hairline,
        borderRadius: 6,
        ...style
      }}
    >
      <SearchIcon style={{ fontSize: 18, color: T.muted, flex: 'none' }} />
      <InputBase
        style={{ flex: 1, fontSize: 14 }}
        placeholder={placeholder}
        value={value}
        inputProps={{ 'aria-label': label }}
        onChange={onChange}
      />
    </div>
  );
}

SearchField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  style: PropTypes.object
};

export function ListNote({ children }) {
  return (
    <div
      style={{
        padding: 32,
        textAlign: 'center',
        fontSize: 14,
        color: T.muted,
        border: '1px dashed ' + T.hairline,
        borderRadius: 6
      }}
    >
      {children}
    </div>
  );
}

ListNote.propTypes = {
  children: PropTypes.node
};

const pagerButton = (disabled) => ({
  fontFamily: 'inherit',
  fontSize: 14,
  height: 36,
  padding: '0 14px',
  borderRadius: 6,
  border: '1px solid ' + T.hairline,
  background: T.elevated,
  color: disabled ? T.mutedSoft : T.ink,
  cursor: disabled ? 'not-allowed' : 'pointer'
});

/** Previous / page of pages / Next. Renders nothing for a single page. */
export function Pager({ page, pageCount, onChange }) {
  if (pageCount <= 1) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16 }}>
      <button
        type='button'
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        style={pagerButton(page <= 1)}
      >
        Previous
      </button>
      <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.muted }}>
        {page} / {pageCount}
      </span>
      <button
        type='button'
        onClick={() => onChange(page + 1)}
        disabled={page >= pageCount}
        style={pagerButton(page >= pageCount)}
      >
        Next
      </button>
    </div>
  );
}

Pager.propTypes = {
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired
};
